import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Badge } from "react-bootstrap";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import Card from "../../../../components/card";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import PaginationComponent from "../../../../components/pagination/pagination";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import { formatLocalDateTime } from "../../../../components/enrollment/format";
import { PaymentIntentListDTO, PaymentIntentSummaryDTO, PAYMENT_INTENT_STATUS } from "../../../../models/EnrollmentHistoryDTO";
import PaymentIntentDetailsModal from "./paymentIntentDetailsModal";
import AccionesAlumno from "../../../../components/enrollment/accionesAlumno";
import { buildPages, errorText, formatDate, formatMoney, HORARIO_LISTA, intentStatusVariant } from "../../managment/enrollmentRequests/helpers";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";
import { contactoDelAlumno, EtiquetaAlumno, nombreDelTicket, SubNombre } from "./delAlumno";
import { tituloEstado } from "../../../../utils/tituloEstado";

type TabKey = "pending" | "processed" | "rejected";
const TAB_STATUSES: Record<TabKey, number[]> = {
	pending: [PAYMENT_INTENT_STATUS.PENDIENTE],
	processed: [PAYMENT_INTENT_STATUS.PROCESADO, PAYMENT_INTENT_STATUS.APLICADO],
	rejected: [PAYMENT_INTENT_STATUS.RECHAZADO]
};
//100 por pantalla (Esteban, 2026-10-20); el servidor no da más de 200. Lo más nuevo va arriba en las 3 pestañas.
const PAGE_SIZES = [100, 200];
const T = {
	title: "Pagos por Procesar",
	tabs: { pending: "Por Procesar", processed: "Procesados", rejected: "Rechazados" },
	empty: "No hay comprobantes en esta bandeja.",
	col: { id: "#", student: "Alumno", enrollment: "Curso / Horario", type: "Forma", note: "Nota del Pago", status: "Estado", createdBy: "Reportó", sales: "Envió", admissions: "Admisiones", caja: "Cajera", createdAt: "Creado" }
};

/** Caja inbox for payments reported through "Solicitudes de matrícula". */
const PaymentIntentsController: React.FC = () => {
	const http = HttpManager.getInstance();
	// v2 session campus is set at login; after a plain page refresh fall back to the legacy campus ref so the screen still works.
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	/*
		Manda el plantel de la BARRA (el que la persona ve), no el guardado en la sesión: los dos se
		desfasaban y Matrículas abría Monterrey estando en San Pedro Sula; el botón Finanzas llevaba
		el plantel equivocado y movía la barra (Esteban, 2026-09-24). El de la sesión es solo respaldo.
	*/
	const currentCampus: any = legacyCampus
		? { id: legacyCampus.id, countryId: legacyCampus.company?.country_id ?? (sessionCampus?.id === legacyCampus.id ? sessionCampus?.countryId : undefined) }
		: sessionCampus;
	const campusId: number | null = currentCampus?.id ?? null;
	const movil = useEsTelefono();

	const [tab, setTab] = useState<TabKey>("pending");
	const [search, setSearch] = useState<string>("");
	const [appliedSearch, setAppliedSearch] = useState<string>("");
	const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0]);
	const [page, setPage] = useState<number>(1);
	const [result, setResult] = useState<PaymentIntentListDTO | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [detailsId, setDetailsId] = useState<number | null>(null);
	//Enlace de un aviso de Cliq: abre el ticket en su plantel (2026-09-25).
	const enlace = useEnlaceDirecto("ticket", (id) => setDetailsId(id));
	const [refreshTick, setRefreshTick] = useState<number>(0);

	useEffect(() => {
		if (campusId === null) {
			return;
		}
		setLoading(true);
		setError(null);
		//Solo vale la respuesta de la última petición: al cambiar de plantel la del anterior puede llegar tarde (2026-09-25).
		let vigente = true;
		http.paymentIntentsService.list(campusId, { statusIds: TAB_STATUSES[tab], search: appliedSearch || undefined }, perPage, page)
			.then((r) => { if (vigente) setResult(r.data); })
			.catch((e) => { if (vigente) setError(errorText(e)); })
			.finally(() => { if (vigente) setLoading(false); });
		return () => { vigente = false; };
	}, [campusId, tab, appliedSearch, perPage, page, refreshTick]);

	const pages = useMemo(() => result ? buildPages(result.currentPage, result.lastPage, (p) => setPage(p), movil) : [], [result, movil]);
	const itemCount = result ? {
		start: result.total === 0 ? 0 : (result.currentPage - 1) * result.perPage + 1,
		end: Math.min(result.currentPage * result.perPage, result.total),
		total: result.total
	} : { start: 0, end: 0, total: 0 };
	const tabs: { key: TabKey; label: string }[] = [
		{ key: "pending", label: T.tabs.pending }, { key: "processed", label: T.tabs.processed }, { key: "rejected", label: T.tabs.rejected }
	];

	if (campusId === null) {
		return <React.Fragment />;
	}

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>{T.title}</h4>
			</div>
			{enlace.aviso ? <div className="alert alert-warning" role="alert" style={{ fontSize: 15, cursor: "pointer" }} onClick={enlace.cerrar}>{enlace.aviso}</div> : ""}
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : ""}
			<Card>
				<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
							{tabs.map((t) => (
								<li className="nav-item" key={t.key}>
									<button type="button" className={"nav-link btn btn-sm " + (tab === t.key ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => { setTab(t.key); setPage(1); }}>{t.label}</button>
								</li>
							))}
						</ul>
						<div style={movil ? { flex: "1 1 100%" } : { minWidth: 320 }}>
							<SearchInputComponent search={search} onChangeSearch={setSearch}
								onClickSearch={() => { setAppliedSearch(search.trim()); setPage(1); }}
								onClearSearch={() => { setSearch(""); setAppliedSearch(""); setPage(1); }} />
						</div>
					</div>
					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 18 }}>
						{loading && !result ? <div className="loader" /> : movil ? (
							/* En teléfono la tabla obliga a arrastrar de lado; misma información en tarjetas. */
							<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
								{!result || !result.data || result.data.length < 1 ? (
									<div style={{ color: "var(--wa-apagado)", textAlign: "center", padding: 24 }}>{T.empty}</div>
								) : result.data.map((r: PaymentIntentSummaryDTO) => (
									<div key={"pim_" + r.id} onClick={() => setDetailsId(r.id)}
										style={{ border: "1px solid var(--wa-filo)", borderRadius: 8, padding: "10px 12px", cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
										<div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
											<b style={{ fontSize: 15 }}>{nombreDelTicket(r)}</b>
											<Badge bg={intentStatusVariant(r.statusId)} style={{ fontWeight: 500, flexShrink: 0 }}>{tituloEstado(r.status)}</Badge>
										</div>
										{r.fromStudent
											? <div style={{ fontSize: 13, marginTop: 2 }}><EtiquetaAlumno /> {r.studentId ? "" : <span style={{ color: "var(--wa-oro)" }}>Sin alumno en WAPP</span>}
												<div style={{ color: "var(--wa-apagado)" }}>{contactoDelAlumno(r)}</div></div>
											: r.kind === "kit"
											? <div style={{ fontSize: 13, marginTop: 2 }}>{r.concept}<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>Producto · pago digital</div></div>
											: <><div style={{ fontSize: 13, marginTop: 2 }}>{cursoCorto(r.courseName)}</div>
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{horarioBonito(r.scheduleName, HORARIO_LISTA)}</div></>}
										{r.notes ? <div style={{ fontSize: 13, marginTop: 4 }}>{(r.notes || "").split("\n")[0]}</div> : ""}
										<div style={{ fontSize: 13, color: "var(--wa-apagado)", marginTop: 4 }}>
											#{r.id} · {formatLocalDateTime(r.createdAt)}
										</div>
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
											{/* Quién lo mandó, quién de Admisiones lo aprobó y a quién de Caja le toca (2026-09-25). */}
											{r.fromStudent ? <>{T.col.caja}: {r.assignedToShort || "-"}</>
												: <>{T.col.sales}: {r.salesShort || r.createdByShort || "-"} · {T.col.admissions}: {r.admissionsShort || "-"} · {T.col.caja}: {r.assignedToShort || "-"}</>}
										</div>
										{r.studentId ? <div style={{ marginTop: 8 }}><AccionesAlumno studentId={r.studentId} campusId={campusId} alinear="inicio" /></div> : ""}
									</div>
								))}
							</div>
						) : (
							<table className="table table-hover" style={{ fontSize: 15, marginBottom: 0 }}>
								<thead>
									<tr>
										<th style={{ width: 52, textAlign: "center" }}>{T.col.id}</th>
										<th style={{ width: 230 }}>{T.col.student}</th>
										<th style={{ width: 250 }}>{T.col.enrollment}</th>
										<th style={{ width: 300 }}>{T.col.note}</th>
										<th style={{ width: 120 }}>{T.col.status}</th>
										<th style={{ width: 120 }}>{T.col.sales}</th>
										<th style={{ width: 120 }}>{T.col.admissions}</th>
										{/* A quién de Caja le tocó por turno; cualquiera del plantel lo puede trabajar (2026-09-25). */}
										<th style={{ width: 120 }}>{T.col.caja}</th>
										<th style={{ width: 120 }}>{T.col.createdAt}</th>
										{/* Única columna sin ancho: se lleva el sobrante, así Nota del pago no se estira (Esteban, 2026-09-23). */}
										<th style={{ paddingRight: 18 }} />
									</tr>
								</thead>
								<tbody>
									{!result || !result.data || result.data.length < 1 ? (
										<tr><td colSpan={10} style={{ color: "var(--wa-apagado)", textAlign: "center", padding: 24 }}>{T.empty}</td></tr>
									) : result.data.map((r: PaymentIntentSummaryDTO) => (
										<tr key={"pi_" + r.id} style={{ cursor: "pointer", opacity: loading ? 0.6 : 1 }} onClick={() => setDetailsId(r.id)}>
											<td style={{ whiteSpace: "nowrap", textAlign: "center" }}>#{r.id}</td>
											<td><b>{nombreDelTicket(r)}</b><SubNombre r={r} /></td>
											<td>{r.fromStudent
												? <><EtiquetaAlumno /><div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{contactoDelAlumno(r)}</div></>
												: r.kind === "kit"
												? <>{r.concept}<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>Producto · pago digital</div></>
												: <>{cursoCorto(r.courseName)}<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{horarioBonito(r.scheduleName, { plural: true, separador: " " })}</div></>}</td>
											<td style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{(r.notes || "-").split("\n")[0]}</td>
											<td><Badge bg={intentStatusVariant(r.statusId)} style={{ fontWeight: 500 }}>{tituloEstado(r.status)}</Badge></td>
											<td style={{ whiteSpace: "nowrap" }}>{r.salesShort || (r.fromStudent ? "Alumno" : "-")}</td>
											<td style={{ whiteSpace: "nowrap" }}>{r.admissionsShort || "-"}</td>
											<td style={{ whiteSpace: "nowrap" }}>{r.assignedToShort || "-"}</td>
											<td style={{ whiteSpace: "nowrap" }}>{formatLocalDateTime(r.createdAt)}</td>
											<td className="celdaAcciones" style={{ paddingRight: 18 }}>{r.studentId ? <AccionesAlumno studentId={r.studentId} campusId={campusId} /> : ""}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</Card>
			<div className="waSobreDock" style={{ marginTop: 10 }}>
				<PaginationComponent pages={pages} pageSizes={PAGE_SIZES} itemCount={itemCount} onPageSizeChange={(size) => { setPerPage(size); setPage(1); }} />
			</div>
			{detailsId !== null ? (
				<PaymentIntentDetailsModal campusId={campusId} countryId={currentCampus.countryId} intentId={detailsId} onClose={() => setDetailsId(null)} onChanged={() => setRefreshTick((t) => t + 1)} />
			) : ""}
		</div>
	);
};

export default PaymentIntentsController;
